import { useActionBarHeight } from "@/hooks/use-action-bar-height";
import { ItineraryCard } from "@/features/purchase-ticket/booking/ItineraryCard";
import { PriceBreakdown } from "@/features/purchase-ticket/booking/PriceBreakDown";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { InvoiceData } from "@/types/purchase-ticket/invoice";
import { toast } from "sonner";
import { ConfirmPurchase } from "@/services/purchase.service";
import { useAuthStore } from "@/stores/auth.store";
import { useState } from "react";
import { useContactStore } from "@/stores/contact.store";

export default function BookingSummaryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentId } = useParams<{ paymentId: string }>();
  const contact = useContactStore((state) => state.contact);

  const purchaseInvoice = location.state?.invoice as InvoiceData | undefined;

  // Expose the fixed action bar height so the layout's bottom-hero sits above it
  const footerRef = useActionBarHeight<HTMLElement>();

  const [loading, setLoading] = useState(false);

  const handleConfirmPurchase = async () => {
    if (loading || !paymentId || !contact) return;

    setLoading(true);

    try {
      const user = useAuthStore.getState().user;

      const response = await ConfirmPurchase({
        agentCompanyId: user?.agentCompanyId ?? "",
        agentCompanySellerId: user?.agentCompanySellerId ?? "",
        paymentId,
        totalPrice: purchaseInvoice?.grandTotal ?? 0,

        paymentMethodList: [
          {
            paymentApproachItemTypeKey: "credit",
            price: purchaseInvoice?.grandTotal ?? 0,
            amountTendered: purchaseInvoice?.grandTotal ?? 0,
            amountChange: 0,
            referenceNo: "",
          },
        ],

        contactName: `${contact.firstName} ${contact.lastName}`.trim(),
        contactEmail: contact.contactEmail,
        mobileNoCountryCode: contact.mobileNoCountryCode,
        mobileNo: contact.contactPhone,
      });

      navigate("/purchase-ticket/booking-confirmation", {
        state: {
          confirmation: response.data,
          paymentId,
        },
      });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to confirm purchase.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      <h1 className="text-secondary-foreground text-lg font-semibold">
        Summary
      </h1>
      <div className="mt-2 md:mt-4">
        <ItineraryCard
          routeName={purchaseInvoice?.routeName ?? "-"}
          departureDate={purchaseInvoice?.departureDateTime}
          returnDate={purchaseInvoice?.returnDateTime}
        />
      </div>

      <h2 className="text-secondary-foreground text-lg font-semibold mt-6">
        Price Breakdown
      </h2>
      <PriceBreakdown
        priceBreakdown={purchaseInvoice?.priceBreakdown ?? []}
        grandTotal={purchaseInvoice?.grandTotal ?? 0}
        className="mt-2"
      />

      <div className="hidden md:flex md:items-center md:justify-end md:mt-6">
        <Button
          type="button"
          variant="secondary"
          className="w-2xs cursor-pointer"
          onClick={handleConfirmPurchase}
        >
          Confirm Purchase
        </Button>
      </div>

      <footer
        ref={footerRef}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8 md:hidden">
          <Button
            type="button"
            variant="secondary"
            className="w-full cursor-pointer"
            onClick={handleConfirmPurchase}
          >
            Confirm Purchase
          </Button>
        </div>
      </footer>
    </div>
  );
}
